/* eslint-disable max-lines */
import individualDiagnose from "../../../share/util/individualDiagnose";
import api from '../../../share/util/api/service';
import constants from '../../../common/nodejs/constants';
import { getAbbrSysName as getAbbrSysNameCommon } from '../../../share/util/systemName';

const TSV_CONTENT_TYPE = 'text/tab-separated-values;charset=utf-8;';
const SYSTEM_SEPARATOR = '\t';

export const HEX_PREFIX = '0x';

// 値の翻訳を実施するデータタイプ
export const VALUE_TRANSLATE_TYPE = ['FLG', 'HEX', 'decExStr'];
// 値の10進数変換を実施するデータタイプ
export const VALUE_CONVERT_DECIMAL_TYPE = [
  'Bin', 'min2hhmm', 'sec2hhmm', 'sec2hhmmss',
  'Lat_DEG', 'Lng_DEG', 'Lat_DMM', 'Lng_DMM', 'LatLng_DMM'
];
// Worker種別
export const WORKER = Object.freeze({
  REALTIME: 'realtime',
  RESTFUL: 'restful',
  SSM5_RESTFUL: 'ssm5_restful',
  MEASUREMENT_CSV: 'measurement_csv',
});
// 親スレッドからWorkerに通知するメッセージ種別
export const WORKER_TASK = Object.freeze({
  START: 'start',
  STOP: 'stop'
});
// Workerから親スレッドに返されるメッセージ種別
export const WORKER_EVENT = Object.freeze({
  SUCCESS_CONNECTION: 'success_connection',
  BASE_TIME: 'baseTime',
  CHUNK_DATA: 'chunkData',
  DECIMATED_DATA: 'decimatedData',
  CSV_DATA: 'csv_data',
  MEASUREMENT_INFO: 'measurementInfo',
  SUCCESS_END: 'success_end',
  STOPPED: 'stopped',
  START_ERROR: 'start_error',
  RUNTIME_ERROR: 'runtime_error',
  MEASUREMENT_NOT_FOUND: 'measurement_not_found',
});
// 呼び出し元を識別する種別
export const TYPE_CALLER = Object.freeze({
  DEFAULT: 'original_dm', // Step3のData Monitor
  AT: 'at_dm',            // Step3のActive Test上のData Monitor
  WS22: 'ws22_dm',        // Work SupportのWS.22で埋め込まれるData Monitor
  WS45: 'ws45_dm',        // Work SupportのWS.45で埋め込まれるData Monitor
  OBD: 'obd_dm',          // OBD版のData Monitor
  MULTI_DM: 'multi_dm',   // マルチデータモニタ
  PLAY: 'play',           // データ再生
  PLAY_WS45: 'play_ws45',       // データ再生(WS.45, 125)
  PLAY_WS146: 'play_ws146',     // データ再生(WS.146)
  MULTI_DM_PLAY: 'mt_dm_play'   // マルチデータモニタ再生
});
// グラフのチェックボックスの列
export const GRAPH_CHECK_COLUMN = Object.freeze({
  SPLIT: 0,
  UNION: 1,
});
// タブ定義
export const TAB = {
  LIST: 0, // リスト
  SPLIT: 1, // 分割グラフ
  UNION: 2, // 結合グラフ
};
// ズーム倍率プルダウンの選択肢
export const ZOOM_DISPLAY_OPTIONS = ['100%', '125%', '150%', '200%', '300%', '400%', '600%', '800%'];
// 表示用ズーム倍率の初期値
export const DEFAULT_ZOOM_DISPLAY_VALUE = '-%';
// 計測データの保存先定義
export const DATA_STORAGE = {
  S3: 's3',
  INTDASH: 'intdash'
};

function downloadFile(blob, fileName) {
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * TSVエクスポート処理
 * @param {Array} data TSVに出力するオブジェクトの配列。先頭オブジェクトのキー名をTSVのヘッダー項目にする。
 * @param {String} fileName ファイル名
 */
export function tsvFileExport(data, fileName) {
  if (!data || data.length === 0) {
    return;
  }

  const tsvData = convertToTSV(data);
  const blob = new Blob([tsvData], { type: TSV_CONTENT_TYPE });
  downloadFile(blob, fileName);
}

/**
 * JSONエクスポート処理
 * @param {Object | Array} data JSON形式のオブジェクトかオブジェクトの配列
 * @param {String} fileName ファイル名
 */
export function jsonFileExport(data, fileName) {
  if (!data) {
    return;
  }

  const blob = new Blob([JSON.stringify(data)], {
    type: 'application/json',
  });
  downloadFile(blob, fileName);
}

/**
 * TSVファイル変換処理
 */
function convertToTSV(data) {
  const tsvArray = [];
  const header = Object.keys(data[0]).map(key => `${key}`).join('\t');
  tsvArray.push(header);
  data.forEach(item => {
    const row = Object.values(item).map(value => `${value}`.replace('\n', '\\n').replace('\t', '\\t')).join('\t');
    tsvArray.push(row);
  });
  return tsvArray.join('\n');
}

/**
 * 物理変換された値から表示する値を取得する
 * @param {object} instance 画面コンポーネントのインスタンス（通常は this）を指定する
 * @param {string} did 選択項目のDID情報
 * @param {string} value 変換された計測値
 * @param {array} conversionInfo 変換情報リスト
 * @param {String|null} region リージョン文字列
 * @param {String} specifiedSystemId 任意のECUの翻訳キーを取得したい場合のみ指定
 * @param {Boolean} hevTargetType null or "Did" or "Rid"
 * @returns 翻訳された計測値
 */
export function getTranslateValue(instance, did, value, conversionInfo, region, specifiedSystemId = null, hevTargetType = null) {
  const cache = new Cache();
  const cachedValue = cache.get(did, value, specifiedSystemId);
  // 0や空文字の場合はキャッシュを利用するため、nullやundefinedの時のみ翻訳情報を取得する
  if (cachedValue !== undefined && cachedValue !== null) {
    return cachedValue;
  }
  // "DID情報_変換結果"（翻訳用jsonを検索する文字列）
  const didValKey = did + "_" + value;

  // diagのjson参照用キー
  const findFFDi18nKey = individualDiagnose.getFFDi18nKeyWithRegion(instance, region, specifiedSystemId);
  const didConversionKey = `${findFFDi18nKey}.`;
  let didValDisp = "";

  if (hevTargetType) {
    // HEV系AT対応 : atから取得、翻訳キーが存在していなければそのまま表示
    didValDisp = instance.$te(`at${specifiedSystemId}.999999.STATUS_${parseInt(value, 16)}`) ?
      instance.$t(`at${specifiedSystemId}.999999.STATUS_${parseInt(value, 16)}`) : value;
  } else if (instance.$te(didConversionKey + didValKey)) {
    // diagに存在する場合、diagから翻訳結果を取得
    didValDisp = instance.$t(didConversionKey + didValKey);
  } else if (instance.$te(`pid.${didValKey}`)) {
    // pidに存在する場合
    didValDisp = instance.$t(`pid.${didValKey}`);
  } else {
    // diag, pidに存在しない場合
    if (conversionInfo.data_type === "decExStr") {
      // decExStrの場合、翻訳結果が無ければ空を返す
      cache.set(did, value, "", specifiedSystemId);
      return "";
    }
    const defaultKey = did + "_default";
    if (instance.$te(didConversionKey + defaultKey)) {
      didValDisp = instance.$t(didConversionKey + defaultKey, [value]);
    } else {
      if (conversionInfo.data_type === "HEX") {
        // HEXで翻訳結果がない場合、先頭に'0x'をつけて返す
        cache.set(did, value, `${HEX_PREFIX}${value}`, specifiedSystemId);
        return `${HEX_PREFIX}${value}`;
      }
      // それ以外の場合、生値を返却
      cache.set(did, value, value, specifiedSystemId);
      return value;
    }
  }

  // diagから翻訳結果を返却
  cache.set(did, value, didValDisp, specifiedSystemId);
  return didValDisp;
}

/**
 * 翻訳対象の種別
 */
export const CACHE_CATEGORY = {
  UNIT: 'unit'
};

/**
 * 選択中のシステム名を取得する
 * @param {object} instance 画面コンポーネントのインスタンス（通常は this）を指定する
 * @returns {array<string>} 選択中の全システム名
 */
export const getSystemIds = (instance) => {
  const indSystemId = individualDiagnose.getCurrentSystemId(instance);
  if (indSystemId) {
    // 個別診断の場合のシステム名
    return [indSystemId];
  } else {
    // 複数診断の場合のシステム名
    return instance.$route.params.systemIds;
  }
};

/**
 * APIなどから渡される信号線項目ごとのIDにシステム名を付与する。
 * @param {string} system システム名
 * @param {string} itemId 項目ID(e.g. 101A_1_all)
 * @returns 項目IDにシステム名を付与したID(e.g. EGI-101A_1_all)
 */
export const setSystem = (system, itemId) => {
  return `${system}${SYSTEM_SEPARATOR}${itemId}`;
};

/**
 * システム名が付与された信号線項目IDを、システム名と項目IDに分割する
 * @param {string} itemId 項目ID(e.g. ABG_25epf-101A_all)
 * @returns {array} 1つ目にシステム名、2つ目に項目IDが入った配列(e.g. ['ABG_25epf', '101A_all'])
 */
export const splitSystemAndItemId = (itemId) => {
  return itemId.split(SYSTEM_SEPARATOR);
};

/**
 * システム名が付与された信号線項目IDから、システム名を取得する
 * @param {string} itemId 項目ID(e.g. ABG_25epf-101A_all)
 * @returns {string} システム名(e.g. AGB_25epf)
 */
export const getSystem = (itemId) => {
  const splitted = splitSystemAndItemId(itemId);
  // システム名の区切り文字が無ければ、システム名が付与されていない
  return splitted.length === 2 ? splitted[0] : '';
};

/**
 * システム名が付与された信号線項目IDから、システムごとの項目IDにばらしたオブジェクトを返す
 * @param {array} itemIds 項目ID(e.g. ['EGI-101A_1_all', 'EGI-1020_2_3', 'ABG_25epf-101A_all'])
 * @returns {object} システムごとの項目ID(e.g. { 'EGI': [{id: '101A_1_all', display_order: 1}, {id: '1020_2_3', display_order: 2}], 'ABG_25epf': [{id: '101A_1_all', display_order: 3}] })
 */
export const getItemsBySystem = (itemIds) => {
  const result = {};
  let display_order = 1;

  for (const itemName of itemIds) {
    const ecuName = itemName.substring(0, itemName.indexOf(SYSTEM_SEPARATOR));
    const itemId = itemName.substring(itemName.indexOf(SYSTEM_SEPARATOR) + 1);
    if (!result[ecuName]) {
      result[ecuName] = [{ id: itemId, display_order: display_order++ }];
    } else {
      result[ecuName].push({ id: itemId, display_order: display_order++ });
    }
  }
  return result;
};

/**
 * システム名が付与された信号線項目IDから、システムごとの信号線項目IDの配列に分割する
 * @param {array} itemIds itemIds 項目ID(e.g. ['EGI-101A_1_all', 'EGI-1020_2_3', 'ABG_25epf-101A_all'])
 * @returns {object} システムごとの項目ID(e.g. { 'EGI': ['101A_1_all', '1020_2_3'], 'ABG_25epf': ['101A_1_all'] })
 */
export const divideBySystem = (itemIds) => {
  const result = {};
  for (const itemName of itemIds) {
    const ecuName = getSystem(itemName);
    if (!result[ecuName]) {
      result[ecuName] = [itemName];
    } else {
      result[ecuName].push(itemName);
    }
  }
  return result;
};

/**
 * APIなどに信号線項目のIDを渡すために、付与したシステム名を外した項目IDを返す
 * @param {string} itemId システム名が付与された項目ID(e.g. EGI-101A_1_all)
 * @returns {string} システム名を外した項目ID(e.g. 101A_1_all)
 */
export const getNoSystemItemId = (itemId) => {
  const splitted = splitSystemAndItemId(itemId);
  // システム名の区切り文字が無ければ、システム名が付与されていない
  return splitted.length === 2 ? splitted[1] : splitted[0];
};

/**
 * 信号線項目IDからDID/PID/RIDのみを返す
 * @param {string} itemId 項目ID(e.g. 101A_1_all or EGI-101A_1_all)
 * @returns {string | null} DID/PID(e.g. 101A)。想定外の形式の場合はnullを返す
 */
export const getDid = (itemId) => {
  const matched = itemId.match(/^(.+)_.+_.+$/);
  if (matched && matched.length > 1 && matched[1][matched[1].length - 2] === "_") {
    return matched[1].slice(0, -2);
  }
  return matched && matched.length > 1 ? matched[1] : null;
};

/**
 * 翻訳結果をキャッシュする
 * $tや$teを1秒に数千回と呼び出すと負荷が高いため、$tの結果を保持しておく
 */
export class Cache {
  translation = {};

  constructor() {
    if (Cache.instance) {
      return Cache.instance;
    }
    Cache.instance = this;
  }

  /**
   * 本クラスの後処理
   */
  destroy() {
    this.translation = {};
    Cache.instance = null;
  }

  /**
   * キャッシュから翻訳情報を取得する
   * @param {string | CACHE_CATEGORY} category DID(信号線項目のID)や翻訳対象の種別
   * @param {string} key 翻訳時のキー
   * @param {string} systemName システム名。指定時はcategoryと合わせて、キャッシュのキーとする。
   * @returns {string | undefined} キャッシュされた翻訳情報。キャッシュが無ければundefined
   */
  get(category, key, systemName = null) {
    const categoryKey = systemName ? setSystem(systemName, category) : category;
    if (!this.translation[categoryKey]) {
      this.translation[categoryKey] = {};
    }
    return this.translation[categoryKey][key];
  }

  /**
   * キャッシュに翻訳結果を設定する
   * @param {string | CACHE_CATEGORY} category DID(信号線項目のID)や翻訳対象の種別
   * @param {string} key 翻訳時のキー
   * @param {string} value 設定する翻訳結果
   * @param {string} systemName システム名。指定時はcategoryと合わせて、キャッシュのキーとする。
   */
  set(category, key, value, systemName = null) {
    const categoryKey = systemName ? setSystem(systemName, category) : category;
    this.translation[categoryKey][key] = value;
  }
}

/**
 * データモニタが提供する機能について、各機能をON/OFFするための設定フラグを管理するクラス。
 * 呼び出し元の機能が追加される場合、呼び出し元の機能側で本クラスを修正すること。
 */
export class DataMonitorFunctionSettings {
  // 呼び出し元画面
  typeCaller = '';
  // 信号線を経由せずに全信号線を自動実行するか否かのフラグ
  isAutoExecute = false;
  // 計測データを永続化するか否かのフラグ ※falseの場合はマーカ登録ボタンも表示しない
  isPersist = true;
  // シークバー／[<<][<][>][>>]ボタンを表示するか否かのフラグ
  isTimeLine = true;
  // 計測ボタンを表示するか否かのフラグ
  isStartButton = true;
  // 停止ボタンを表示するか否かのフラグ
  isStopButton = true;
  // 戻るボタンを表示するか否かのフラグ
  isBackButton = true;
  // システム選択プルダウンを表示するか否かのフラグ
  isSystemSelect = false;
  // システム列を表示するか否かのフラグ
  isSystemRow = false;

  /**
   * コンストラクタ
   * @param {string} type_caller 呼び出し元
   */
  constructor(type_caller) {
    this.typeCaller = type_caller;
    switch (type_caller) {
      // WS.22
      case TYPE_CALLER.WS22:
        this.isAutoExecute = true;    // 自動実行する
        this.isPersist = false;       // 永続化しない
        this.isTimeLine = false;      // シークバー／[<<][<][>][>>]ボタンを表示しない
        this.isStartButton = false;   // 計測ボタンを表示しない
        this.isStopButton = false;    // 停止ボタンを表示しない
        this.isBackButton = false;    // 戻るボタンを表示しない
        break;
      // WS.45/125
      case TYPE_CALLER.WS45:
        this.isAutoExecute = false;   // 自動実行しない
        this.isPersist = true;        // 永続化する
        this.isTimeLine = true;       // シークバーを表示する
        this.isStartButton = false;   // 計測ボタンを表示しない
        this.isStopButton = false;    // 停止ボタンを表示しない
        this.isBackButton = false;    // 戻るボタンを表示しない
        break;
      // OBD
      case TYPE_CALLER.OBD:
        this.isAutoExecute = false;   // 自動実行しない
        this.isPersist = true;        // 永続化する ※条件によってfalseの場合あり(呼び元で上書き)
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = true;    // 計測ボタンを表示する
        this.isStopButton = true;     // 停止ボタンを表示する
        this.isBackButton = true;     // 戻るボタンを表示する
        break;
      // データ再生
      case TYPE_CALLER.PLAY:
        this.isAutoExecute = false;   // 自動実行しない ※再生＝測定なし
        this.isPersist = true;        // 永続化する ※再生＝永続化あり
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = false;   // 計測ボタンを表示しない
        this.isStopButton = false;    // 停止ボタンを表示しない
        this.isBackButton = true;     // 戻るボタンを表示する
        break;
      // データ再生(WS.45/125/146)
      case TYPE_CALLER.PLAY_WS45:
      case TYPE_CALLER.PLAY_WS146:
        this.isAutoExecute = false;   // 自動実行しない
        this.isPersist = true;        // マーカーボタンを表示する
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = false;   // 計測ボタンを表示しない
        this.isStopButton = false;    // 停止ボタンを表示しない
        this.isBackButton = true;     // 戻るボタンを表示する(スペース確保のため)
        break;
      // マルチデータモニタ
      case TYPE_CALLER.MULTI_DM:
        this.isAutoExecute = false;   // 自動実行しない
        this.isPersist = true;        // 永続化する
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = true;    // 計測ボタンを表示する
        this.isStopButton = true;     // 停止ボタンを表示する
        this.isBackButton = true;     // 戻るボタンを表示する
        this.isSystemSelect = true;   // システム選択プルダウンを表示する
        this.isSystemRow = true;      // システム列を表示する
        break;
      // マルチデータモニタ再生
      case TYPE_CALLER.MULTI_DM_PLAY:
        this.isAutoExecute = false;   // 自動実行しない ※再生＝測定なし
        this.isPersist = true;        // 永続化する ※再生＝永続化あり
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = false;   // 計測ボタンを表示しない
        this.isStopButton = false;    // 停止ボタンを表示しない
        this.isBackButton = true;     // 戻るボタンを表示する
        this.isSystemSelect = false;  // システム選択プルダウンを表示しない
        this.isSystemRow = true;      // システム列を表示する
        break;
      // 通常のDM、その他
      case TYPE_CALLER.DEFAULT:
      case TYPE_CALLER.AT:
      default:
        this.isAutoExecute = false;   // 自動実行しない
        this.isPersist = true;        // 永続化する
        this.isTimeLine = true;       // シークバー／[<<][<][>][>>]ボタンを表示する
        this.isStartButton = true;    // 計測ボタンを表示する
        this.isStopButton = true;     // 停止ボタンを表示する
        this.isBackButton = true;     // 戻るボタンを表示する
        break;
    }
  }

  /**
   * 呼び出し元画面が再生画面か否かを返す
   * @returns {boolean} 再生画面か否か
   */
  isPlay() {
    return [TYPE_CALLER.PLAY, TYPE_CALLER.MULTI_DM_PLAY, TYPE_CALLER.PLAY_WS45].includes(this.typeCaller);
  }

  /**
   * 呼び出し元画面がマルチデータモニタ画面か否かを返す
   * @returns {boolean} マルチデータモニタ画面か否か
   */
  isMultipleDataMonitor() {
    return [TYPE_CALLER.MULTI_DM, TYPE_CALLER.MULTI_DM_PLAY].includes(this.typeCaller);
  }

  /**
   * 呼び出し元画面のIDを取得する
   * @returns {string} 呼び出し元画面のID
   */
  getTypeCaller() {
    return this.typeCaller;
  }

  /**
   * 呼び出し元画面がWS.22/45/125の計測画面か否かを返す
   * @returns {boolean} WS.22/45/125の計測画面ならtrue、そうでなければfalse
   */
  isWsExe() {
    return [TYPE_CALLER.WS22, TYPE_CALLER.WS45].includes(this.typeCaller);
  }
}

/**
 * グラフごとに使用している色を管理するクラス
 */
export class ColorManager {
  COLOR_LIST = [
    'hsla(356, 98%, 48%, 1)',
    'hsla(27, 100%, 55%, 1)',
    'hsla(55, 100%, 39%, 1)',
    'hsla(112, 90%, 39%, 1)',
    'hsla(203, 90%, 45%, 1)',
    'hsla(242, 100%, 39%, 1)',
    'hsla(262, 98%, 50%, 1)',
    'hsla(296, 100%, 46%, 1)',
    'hsla(170, 75%, 34%, 1)',
    'hsla(80, 65%, 40%, 1)',
    'hsla(15, 75%, 60%, 1)',
    'hsla(220, 70%, 58%, 1)',
    'hsla(335, 70%, 38%, 1)',
    'hsla(45, 90%, 52%, 1)',
    'hsla(255, 80%, 32%, 1)',
    'hsla(195, 85%, 32%, 1)',
  ];

  // Bootstrapのbodyの文字色
  static DEFAULT_COLOR = '#212529';

  /**
   * グラフごとに、全ての色の使用状況を保持する台帳。
   */
  register = { [TAB.SPLIT]: [], [TAB.UNION]: [] }

  /**
   * グラフごとに、使用者をキーとして使用されている色を保持する。
   * 使用するコンポーネント側で使用者をキーで引きたいため、別途使用状況を保持。
   */
  usingStatus = { [TAB.SPLIT]: {}, [TAB.UNION]: {} };

  constructor() {
    this.clearRegister();
  }

  /**
   * 使用可能な色を借りる
   * @param {number} graphType "TAB"定数の値
   * @param {string} itemId 項目ID (e.g. 'ABG_25ePF\t101A_1_all')
   * @returns {string} 色定義の文字列 (e.g. 'hsla(356, 98%, 48%, 1)')
   */
  borrowColor(graphType, itemId) {
    const availableIndex = this._getAvailableIndex(graphType);
    if (availableIndex === -1) {
      console.warn(`No available color. ${graphType} ${itemId}`);
      return ColorManager.DEFAULT_COLOR;
    }
    this._updateRegister(graphType, availableIndex, itemId);
    return this.register[graphType][availableIndex].color;
  }

  /**
   * 使用終了した色を返却する
   * @param {number} graphType "TAB"定数の値
   * @param {string} itemId 項目ID (e.g. 'ABG_25ePF\t101A_1_all')
   */
  returnColor(graphType, itemId) {
    const usingIndex = this._getUsingIndex(graphType, itemId);
    if (usingIndex !== -1) {
      this._updateRegister(graphType, usingIndex);
    }
  }

  /**
   * ユーザーが設定した色を使用状況に反映する
   * @param {number} graphType "TAB"定数の値
   * @param {string} itemId 項目ID (e.g. 'ABG_25ePF\t101A_1_all')
   * @param {string} color 色定義の文字列 (e.g. 'hsla(356, 18%, 85%, 1.00)')
   */
  registerColor(graphType, itemId, color) {
    // 色を借りている場合は返却する
    this.returnColor(graphType, itemId);
    this.usingStatus[graphType][itemId] = color;
  }

  /**
   * ユーザーが設定した色を使用状況から削除する
   * @param {number} graphType "TAB"定数の値
   * @param {string} itemId 項目ID (e.g. 'ABG_25ePF\t101A_1_all')
   */
  removeColor(graphType, itemId) {
    if (this.usingStatus[graphType][itemId]) {
      delete this.usingStatus[graphType][itemId];
    }
  }

  /**
   * 使用者と使用中の色を取得する
   * @param {number} graphType "TAB"定数の値
   * @returns {object} キーに使用者、値に使用中の色を持つオブジェクト
   */
  getUsingColors(graphType) {
    return this.usingStatus[graphType];
  }

  /**
   * 色の使用状況をクリアする
   */
  clearRegister() {
    this.register = {
      [TAB.SPLIT]: this.COLOR_LIST.map(color => ({ color: color, borrower: null })),
      [TAB.UNION]: this.COLOR_LIST.map(color => ({ color: color, borrower: null })),
    };
    this.usingStatus = { [TAB.SPLIT]: {}, [TAB.UNION]: {} };
  }

  static getDefaultColor() {
    return ColorManager.DEFAULT_COLOR;
  }

  _getAvailableIndex(graphType) {
    return this.register[graphType]?.findIndex(colorStatus => colorStatus.borrower === null);
  }

  _getUsingIndex(graphType, borrower) {
    return this.register[graphType]?.findIndex(colorStatus => colorStatus.borrower === borrower);
  }

  _updateRegister(graphType, index, borrower = null) {
    const currentBorrower = this.register[graphType][index]?.borrower;
    this.register[graphType][index] = {
      ...this.register[graphType][index],
      borrower: borrower
    };

    // 台帳更新に合わせて、使用状況も更新
    if (borrower) {
      this.usingStatus[graphType][borrower] = this.register[graphType][index].color;
    } else {
      currentBorrower && delete this.usingStatus[graphType][currentBorrower];
    }
  }
}

/**
* システムの略称を取得する
* @param {string} systemName システム名
* @returns {string} システム略称
*/
export const getAbbrSysName = (systemName) => {
  return getAbbrSysNameCommon(systemName);
};

/**
 * Phase6ATのうち、IO/Routine Control結果を否定応答を用いてDM領域に表示する機能かどうかを返す
 * @param {string} atId 要求ID
 * @returns 否定応答を用いてDM領域に表示する機能かどうか
 */
export const isPhase6At = (atId) => {
  switch (atId) {
    case "126":
    case "127":
      return true;
    default:
      return false;
  }
};

/**
 * パーセンテージ計算処理
 * @param {number} allCount 全体数
 * @param {number} currentCount 現在の数
 * @returns {number} 計算したパーセンテージ
 */
export const calculatePercentage = (allCount, currentCount) => {
  if (!currentCount) {
    return 0;
  }
  const percentage = Math.trunc(currentCount / allCount * 100);
  return percentage > 100 ? 100 : percentage;
};

/**
 * project情報を取得する
 * @param {string} apiServiceName APIのサービス名
 * @param {string} endpoint APIのエンドポイント
 * @param {object} vehicleInfo 車両情報
 * @param {string} measurementId 計測UUID
 * @param {boolean|null} isTestMode テストモードか否か
 * @param {object|null} memoDataForTrainingMode トレーニングモード時のメモデータ
 * @returns {Promise<object>} project情報
 */
export const fetchProjectDetails = async (apiServiceName, endpoint, vehicleInfo, measurementId, isTestMode = null, memoDataForTrainingMode = null) => {
  // ミラーサイトの判定
  const selectedDatasource = process.env.VUE_APP_BACKUP_TYPE ? constants.MAINTENANCE_ACTIVE_ENV_MIRROR : constants.MAINTENANCE_ACTIVE_ENV_DEFAULT;
  const res = await api.getParamCall(apiServiceName, endpoint, { region_info: selectedDatasource });
  let data = res.data.measures.find(item => item.measurement_id === measurementId);
  if (isTestMode) {
    // トレーニングモードの場合は固定のレスポンスのため、0番目の要素を使用する
    data = { ...res.data.measures[0] };

    // トレーニングモード用のメモデータがある場合は上書き
    if (memoDataForTrainingMode) {
      data.memo = memoDataForTrainingMode.memo;
      data.data_name = memoDataForTrainingMode.dataName;
    }
  }
  const dataName = data.data_name;

  // エクスポートに必要な情報に整形
  const projectDetails = {
    ...vehicleInfo.vehicle.vin,
    information: {
      ...vehicleInfo.vehicle.spec,
      vin: vehicleInfo.vehicle.vin.vin,
      vehicle_name: vehicleInfo.vehicle.spec.model
    },
    measures: [
      {
        [apiServiceName]: [
          {
            ...data,
            name: dataName,
            note: data.memo
          }
        ]
      }
    ]
  };
  return { dataName, projectDetails };
};

/**
 * グラフズーム機能の有効/無効を設定する
 * @param {array} graphMediator タブごとのGraphMediator配列
 * @param {boolean} enabled true: 有効, false: 無効
 */
export const setGraphZoomEnabled = (graphMediator, enabled) => {
  [TAB.SPLIT, TAB.UNION].forEach((tabId) => {
    const mediator = graphMediator?.[tabId];
    if (!mediator?.getGraphElementList) {
      return;
    }
    mediator.getGraphElementList().forEach((graphElement) => {
      graphElement.setZoomPluginEnabled?.(enabled);
    });
  });
};

/**
 * 選択中のグラフ項目数を更新する
 * @param {number} type グラフの種類(0:分割G, 1:結合G)
 * @param {boolean} isSelect グラフを追加した場合はtrue、削除した場合はfalse
 * @param {array} selectedGraphLines 選択中のグラフ項目数を保持する配列
 * @param {object} maxLines グラフの最大表示数
 */
export const updateSelectedGraphLinesCount = (type, isSelect, selectedGraphLines, maxLines) => {
  if (isSelect) {
    // グラフ追加時は最大値を超えないようにする
    const maxLinesValue = (type === 0) ? maxLines.SPLIT : maxLines.UNION;
    if (maxLinesValue > selectedGraphLines[type + 1]) {
      selectedGraphLines[type + 1]++;
    }
    return;
  }

  // 以降はグラフ項目削除時の処理
  if (selectedGraphLines[type + 1] > 0) {
    selectedGraphLines[type + 1]--;
  }
};

export default {
  VALUE_TRANSLATE_TYPE,
  VALUE_CONVERT_DECIMAL_TYPE,
  CACHE_CATEGORY,
  HEX_PREFIX,
  TYPE_CALLER,
  GRAPH_CHECK_COLUMN,
  TAB,
  DATA_STORAGE,
  tsvFileExport,
  jsonFileExport,
  getTranslateValue,
  getSystemIds,
  setSystem,
  splitSystemAndItemId,
  getSystem,
  getItemsBySystem,
  getNoSystemItemId,
  getDid,
  getAbbrSysName,
  isPhase6At,
  calculatePercentage,
  setGraphZoomEnabled,
  fetchProjectDetails,
  updateSelectedGraphLinesCount,
  Cache,
  DataMonitorFunctionSettings,
  ColorManager
};